export const box = (checked) => (checked ? "☒" : "☐");

// Provenance line shown under an outcome / support title on screen — the note
// count is part of the record, so it belongs in the export too.
export const metaLine = (item) =>
  [item?.meta, item?.noteCount != null ? `Drafted from ${item.noteCount} notes` : ""].filter(Boolean).join(" · ");

export const statusLine = (status) =>
  `${box(status === "achieved")} Achieved   ${box(status === "on-track")} On track   ${box(
    status === "limited"
  )} Limited or no progress`;

export const planChangeLine = (item) =>
  `Plan change needed? ${box(item?.planChangeNeeded)} Yes  ${box(!item?.planChangeNeeded)} No${
    item?.planChangeNeeded && item?.planChangeDescription ? ` — ${item.planChangeDescription}` : ""
  }`;

// A signer with a signature on file stores an image URL rather than a name, so
// the completion block has to render it as a picture instead of as text.
export const isImageSignature = (value) =>
  typeof value === "string" && /^(https?:\/\/|data:image\/)/i.test(value.trim());

// One image is fetched repeatedly — the preview re-captures on every edit, the
// download captures again, and the provider logo is redrawn on every page — so
// the fetched bytes are kept, keyed by URL.
const inlinedImages = new Map();

const fetchAsDataUrl = async (url) => {
  // cache: "no-store" matters as much as the fetch itself — see below.
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) throw new Error(`Image responded ${response.status}`);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
};

// Cached data: URL for a remote image, or null if it can't be fetched. jsPDF's
// addImage cannot take a URL, so the provider logo has to be resolved to bytes
// before the header can draw it.
export const loadImageAsDataUrl = async (url) => {
  if (typeof url !== "string" || !url.trim()) return null;
  const key = url.trim();
  if (/^data:/i.test(key)) return key;
  if (inlinedImages.has(key)) return inlinedImages.get(key);
  try {
    const dataUrl = await fetchAsDataUrl(key);
    inlinedImages.set(key, dataUrl);
    return dataUrl;
  } catch {
    // Cache the failure too: a missing logo must not re-request on every page
    // of every export.
    inlinedImages.set(key, null);
    return null;
  }
};

// Swaps a remote (S3) signature for an inline data: URL before the document is
// rasterized.
//
// html2canvas rasterizes a *clone* of the document and re-requests every image
// in it with crossOrigin set. The review screen has already loaded the very
// same signature URL through a plain <img> with no crossOrigin, so the browser
// holds a cached response carrying no CORS headers — and it serves that cached
// response to the crossOrigin re-request, which then fails the CORS check. The
// image silently drops out of the capture, which is why a signature visible on
// the review screen was missing from both the preview and the downloaded PDF.
// Fetching the bytes here (bypassing that poisoned cache entry) and embedding
// them removes the cross-origin load from the capture altogether.
export const inlineSignatureImage = async (draft) => {
  const value = draft?.signature?.signature;
  if (typeof value !== "string" || !isImageSignature(value) || /^data:/i.test(value.trim())) return draft;
  const url = value.trim();
  const dataUrl = await loadImageAsDataUrl(url);
  // Keep the remote URL when the fetch failed: html2canvas may still manage it
  // (a cold cache, or a bucket answering CORS), and a report missing only its
  // signature image is better than no document at all.
  if (!dataUrl) return draft;
  return { ...draft, signature: { ...draft.signature, signature: dataUrl } };
};
